import { GoogleGlobalSiteTagPageCommand } from 'backend';
import {
  EXTERNAL_MEASUREMENT_TAG_TYPE,
  GTAG_INITIALIZE_SCRIPT_ELEMENT_ID,
  GTAG_LOAD_SCRIPT_ELEMENT_ID,
  GTAG_PAGE_COMMAND_TYPE,
  MeasurementSettingHandledByGtag,
} from '../models';
import { createWithSendTo, getId } from './shared';

const baseConfig = {
  send_page_view: false,
};

const createConfigsString = (
  settings: MeasurementSettingHandledByGtag[]
): string => {
  const configScriptStrings = settings.map((setting) => {
    if (setting.type === EXTERNAL_MEASUREMENT_TAG_TYPE.UniversalAnalytics) {
      const { crossDomainTrackingSettings, trackingId } = setting;
      const config = crossDomainTrackingSettings?.length
        ? {
            ...baseConfig,
            linker: {
              domains: crossDomainTrackingSettings,
            },
          }
        : baseConfig;
      return `gtag('config', '${trackingId}', ${JSON.stringify(config)});`;
    }
    const { measurementId } = setting;
    return `gtag('config', '${measurementId}', ${JSON.stringify(baseConfig)});`;
  });
  return configScriptStrings.join('\n');
};

const createExecuteString = (
  id: string,
  command: GoogleGlobalSiteTagPageCommand
): string => {
  const withSendTo = createWithSendTo(id);
  switch (command.commandType) {
    case GTAG_PAGE_COMMAND_TYPE.config: {
      const { commandType, additionalConfig } = command;
      additionalConfig;
      return `gtag('${commandType}', '${id}', ${JSON.stringify(
        additionalConfig
      )});`;
    }
    case GTAG_PAGE_COMMAND_TYPE.consent: {
      const { commandType, consentArg, consentParams } = command;
      return `gtag('${commandType}', '${consentArg}', ${JSON.stringify(
        consentParams
      )});`;
    }
    case GTAG_PAGE_COMMAND_TYPE.event: {
      const { commandType, eventName, eventParameterValues } = command;
      if (eventParameterValues) {
        return `gtag('${commandType}', '${eventName}', ${JSON.stringify(
          withSendTo(eventParameterValues)
        )});`;
      }
      return `gtag('${commandType}', '${eventName}')`;
    }
    case GTAG_PAGE_COMMAND_TYPE.get: {
      // getはcallback関数が必要だが、evalするわけにもいかないため無視する
      return '';
    }
    case GTAG_PAGE_COMMAND_TYPE.set: {
      const { commandType, parameterValues } = command;
      return `gtag('${commandType}', '${JSON.stringify(
        withSendTo(parameterValues)
      )}');`;
    }
  }
};

const createEventsString = (
  settings: MeasurementSettingHandledByGtag[]
): string => {
  const eventScriptStrings = settings.map((setting) => {
    const { pageCommands } = setting;
    if (!pageCommands.length) {
      return '';
    }
    const id = getId(setting);
    return pageCommands
      .map((pageCommand) => {
        return createExecuteString(id, pageCommand);
      })
      .join('\n');
  });
  return eventScriptStrings.join('\n');
};

type InitializeGtagPayload = {
  settings: MeasurementSettingHandledByGtag[];
};

export const initializeGtag = ({ settings }: InitializeGtagPayload): void => {
  const loadId = getId(settings[0]);
  const loadScriptElement = document.createElement('script');
  loadScriptElement.async = true;
  loadScriptElement.id = GTAG_LOAD_SCRIPT_ELEMENT_ID;
  loadScriptElement.src = `https://www.googletagmanager.com/gtag/js?id=${loadId}`;
  document.body.appendChild(loadScriptElement);

  const config = createConfigsString(settings);
  const event = createEventsString(settings);

  const initializeScript = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    ${config}
    ${event}
  `;
  const initializeScriptElement = document.createElement('script');
  initializeScriptElement.id = GTAG_INITIALIZE_SCRIPT_ELEMENT_ID;
  initializeScriptElement.text = initializeScript;
  document.body.appendChild(initializeScriptElement);
};
